import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  DEFAULT_SKIN_ID,
  legacySkin,
  resolveSkin,
  resolveSkinAsset,
  resolveSkinVariant,
} from "./registry";
import { SkinDefinition, SkinId, SkinRuntime } from "./types";

export interface SkinRootProps {
  skinId?: string;
}

const SkinContext = createContext<SkinRuntime>({
  skin: resolveSkin(DEFAULT_SKIN_ID),
});

const SKIN_TOKEN_STYLE_ID = "app-skin-tokens";

const createSkinTokenCss = (skin: SkinDefinition) => {
  const tokenRules = Object.entries(skin.tokens)
    .map(([tokenName, tokenValue]) => `  ${tokenName}: ${tokenValue};`)
    .join("\n");

  return `html[data-app-skin="${skin.id}"] {\n${tokenRules}\n}`;
};

export const applySkinToDocument = (skin: SkinDefinition) => {
  if (typeof document === "undefined") {
    return () => undefined;
  }

  const attributeRoots = [document.documentElement, document.body];
  const tokenNames = Object.keys(skin.tokens);
  const styleElement =
    document.getElementById(SKIN_TOKEN_STYLE_ID) ||
    document.createElement("style");

  styleElement.id = SKIN_TOKEN_STYLE_ID;
  styleElement.setAttribute("data-app-skin", skin.id);
  styleElement.textContent = createSkinTokenCss(skin);
  document.head.appendChild(styleElement);

  attributeRoots.forEach((root) => {
    root.dataset.appSkin = skin.id;
  });

  tokenNames.forEach((tokenName) => {
    document.documentElement.style.removeProperty(tokenName);
    document.body.style.removeProperty(tokenName);
  });

  return () => {
    attributeRoots.forEach((root) => {
      if (root.dataset.appSkin === skin.id) {
        delete root.dataset.appSkin;
      }
    });

    tokenNames.forEach((tokenName) => {
      document.documentElement.style.removeProperty(tokenName);
      document.body.style.removeProperty(tokenName);
    });

    if (styleElement.getAttribute("data-app-skin") === skin.id) {
      styleElement.remove();
    }
  };
};

export const SkinRoot = ({
  skinId = DEFAULT_SKIN_ID,
  children,
}: PropsWithChildren<SkinRootProps>) => {
  const skin = useMemo(() => resolveSkin(skinId), [skinId]);
  const runtime = useMemo(() => ({ skin }), [skin]);

  useEffect(() => applySkinToDocument(skin), [skin]);

  return (
    <SkinContext.Provider value={runtime}>{children}</SkinContext.Provider>
  );
};

export const useSkin = () => useContext(SkinContext);

export const useSkinId = (): SkinId => {
  return useSkin().skin.id;
};

export const useSkinAsset = (assetKey: string): string | null => {
  const { skin } = useSkin();
  return useMemo(() => resolveSkinAsset(skin, assetKey, legacySkin), [
    assetKey,
    skin,
  ]);
};

export function useSkinVariant<TValue>(
  variants: Partial<Record<SkinId, TValue>>,
  fallback?: TValue
): TValue | null {
  const { skin } = useSkin();
  return useMemo(() => resolveSkinVariant(skin, variants, fallback), [
    fallback,
    skin,
    variants,
  ]);
}
